import React, { useEffect, useState, useRef } from 'react';
import { videoAPI } from '../lib/api';
import { ChevronDown, ChevronUp, Play, Pause, Check, User, Users, Sparkles } from 'lucide-react';
import { useTranslation } from '../lib/i18n';

/**
 * Voice picker for the Auto-Post settings page.
 *
 * Shows the curated ElevenLabs preset voices as selectable cards (with a
 * tap-to-hear sample) — the same set offered in onboarding — PLUS an
 * "own voice" advanced option where a paid user can paste a cloned voice_id.
 *
 * `value`     — current voice_id: a preset key ("matilda") or a raw custom id
 * `onChange`  — called with the new voice_id (or null to reset to default)
 * `disabled`  — greyed out when voiceover itself is switched off
 */
export default function VoiceSelectSection({ value, onChange, disabled }) {
  const { lang } = useTranslation();
  const [voices, setVoices] = useState([]);
  const [defaultKey, setDefaultKey] = useState('matilda');
  const [loading, setLoading] = useState(true);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [customId, setCustomId] = useState('');
  const [playingKey, setPlayingKey] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await videoAPI.getVoicePresets();
        if (cancelled) return;
        // API returns { voices: [...], default_key: "matilda" }.
        setVoices(res.data?.voices || []);
        setDefaultKey(res.data?.default_key || 'matilda');
      } catch (_) {
        /* non-fatal — the advanced own-voice field still works */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Cleanup audio on unmount
  useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);

  const presetKeys = voices.map((v) => v.key);
  const isCustom = !!value && presetKeys.length > 0 && !presetKeys.includes(value);
  const activeKey = isCustom ? null : (value || defaultKey);

  // Reflect an already-saved custom id into the advanced field on load.
  useEffect(() => {
    if (isCustom) { setAdvancedOpen(true); setCustomId(value); }
  }, [isCustom, value]);

  const toggleSample = (key, url) => {
    if (!url) return;
    if (playingKey === key) {
      if (audioRef.current) audioRef.current.pause();
      setPlayingKey(null);
      return;
    }
    if (audioRef.current) audioRef.current.pause();
    const a = new Audio(url);
    a.onended = () => setPlayingKey(null);
    a.play().catch(() => {});
    audioRef.current = a;
    setPlayingKey(key);
  };

  return (
    <div className={`mt-3 space-y-2.5 ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-[#4a463b]">Stimme</span>
        <span className="text-[11px] text-[#8a8474]">Tippen zum Auswählen · ▶ zum Anhören</span>
      </div>

      {loading ? (
        <div className="text-xs text-[#8a8474] py-3 text-center">Stimmen werden geladen …</div>
      ) : voices.length === 0 ? (
        <div className="text-xs text-[#8a8474] py-3 text-center px-3">
          Stimmen konnten nicht geladen werden. Du kannst unten deine eigene Stimme verwenden.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {voices.map((voice) => {
            const isActive = activeKey === voice.key;
            const isPlaying = playingKey === voice.key;
            const Icon = voice.gender === 'female' ? User : Users;
            const description = lang === 'en' ? voice.description_en : voice.description_de;
            return (
              <div
                key={voice.key}
                role="button"
                tabIndex={0}
                onClick={() => onChange(voice.key)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChange(voice.key); } }}
                className={`p-2.5 rounded-xl border-2 cursor-pointer transition-all ${
                  isActive
                    ? 'border-[#1f4d3a] bg-[#e9efe7]'
                    : 'border-[#d8d0bd]/80 bg-[#f2ecdf] hover:border-[#1f4d3a]/50'
                }`}
                data-testid={`voice-card-${voice.key}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className={`p-1.5 rounded-md shrink-0 ${
                      voice.gender === 'female' ? 'bg-pink-500/20 text-pink-300' : 'bg-blue-500/20 text-blue-300'
                    }`}>
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-sm font-medium text-[#23211a] truncate">{voice.name}</span>
                  </div>
                  {isActive && <Check className="w-4 h-4 text-[#1f4d3a] shrink-0" />}
                </div>
                <p className="text-[11px] text-[#6f6a5c] mt-1 leading-tight line-clamp-2">{description}</p>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); toggleSample(voice.key, voice.sample_url); }}
                  aria-label={isPlaying ? 'Stop' : 'Anhören'}
                  className={`mt-2 w-full inline-flex items-center justify-center gap-1.5 px-2 py-1 rounded-md text-[11px] transition ${
                    isPlaying
                      ? 'bg-[#1f4d3a]/30 text-[#23211a] ring-1 ring-[#1f4d3a]/60'
                      : 'bg-[#efe9db] hover:bg-[#efe9db] text-[#4a463b]'
                  }`}
                >
                  {isPlaying ? <><Pause className="w-3 h-3" /> Stop</> : <><Play className="w-3 h-3" /> Anhören</>}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Own voice (advanced) */}
      <button
        type="button"
        onClick={() => setAdvancedOpen((v) => !v)}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border-2 transition ${
          isCustom ? 'border-[#1f4d3a] bg-[#e9efe7]' : 'border-[#d8d0bd]/80 bg-[#f2ecdf] hover:border-[#1f4d3a]/50'
        }`}
      >
        <span className="flex items-center gap-2 text-sm text-[#23211a]">
          <Sparkles className="w-4 h-4 text-[#1f4d3a]" />
          Eigene Stimme {isCustom && <Check className="w-4 h-4 text-[#1f4d3a]" />}
        </span>
        {advancedOpen ? <ChevronUp className="w-4 h-4 text-[#6f6a5c]" /> : <ChevronDown className="w-4 h-4 text-[#6f6a5c]" />}
      </button>

      {advancedOpen && (
        <div className="rounded-lg border border-[#e3dccb] bg-[#f2ecdf] p-3 space-y-2.5">
          <p className="text-[11px] text-[#4a463b] leading-relaxed font-medium">
            Eigene Stimme verwenden (z.&nbsp;B. dein eigener Voice-Clone)
          </p>
          <ol className="text-[11px] text-[#6f6a5c] leading-relaxed list-decimal pl-4 space-y-1">
            <li>Kostenloses Konto auf <a href="https://elevenlabs.io" target="_blank" rel="noreferrer" className="text-[#1f4d3a] underline underline-offset-2">elevenlabs.io</a> erstellen.</li>
            <li><span className="text-[#4a463b]">Voices → Add a new voice → Instant Voice Clone</span>, ca. 1&nbsp;Minute Audio von dir hochladen.</li>
            <li>Die fertige Stimme öffnen und <span className="text-[#4a463b]">„Copy Voice ID"</span> anklicken.</li>
            <li>Die kopierte voice_id unten einfügen.</li>
          </ol>
          <input
            type="text"
            value={customId}
            onChange={(e) => setCustomId(e.target.value)}
            onBlur={() => { const v = (customId || '').trim(); if (v) onChange(v); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); const v = (customId || '').trim(); if (v) onChange(v); } }}
            placeholder="z.B. XrExE9yKIg1WjnnlVkGX"
            data-testid="voice-custom-id-input"
            className="w-full bg-[#f7f3ea]/60 border border-[#d8d0bd] rounded-md px-3 py-1.5 text-xs text-[#23211a] placeholder:text-[#9c9585] focus:outline-none focus:ring-2 focus:ring-[#1f4d3a]/40 font-mono"
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
          />
          <div className="flex items-center justify-between gap-2">
            <p className="text-[10px] text-[#8a8474] leading-relaxed">
              Format wie <span className="font-mono text-[#6f6a5c]">XrExE9yKIg1WjnnlVkGX</span>. Nur in Autopilot &amp; Team.
            </p>
            {isCustom && (
              <button
                type="button"
                onClick={() => { setCustomId(''); onChange(defaultKey); }}
                className="text-[10px] text-[#8a8474] hover:text-[#4a463b] underline underline-offset-2 flex-shrink-0"
              >
                Auf Standardstimme zurück
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
